import type { WrapProps } from '@leafwell/components';
import type { Page } from 'types';
import React from 'react';
import Image from 'next/image';
import { Heading, Paragraph, Wrap } from '@leafwell/components';
import HeadingBody from './heading-body';
import WysiwygContent from './wysiwyg-content';
import PartnerForm from './partner-form';
import PartnerScripts from './partner-scripts';

const HeroPartner: React.FC<
  Partial<Pick<WrapProps, 'className' | 'spacer'>> &
    Pick<Page['acf'], 'hero' | 'pageType'> & {
      showBadges?: boolean;
    }
> = ({
  className = '',
  spacer = true,
  hero: { title, description, subTitle, badges, formLabel },
  showBadges = true,
}) => {
  return (
    <section className="bg-lilac-50 leafwell-partners-hero xl:-mt-40 xl:pt-40">
      <Wrap
        className={[
          'relative md:items-start md:gap-x-16 xl:gap-x-24 lg:grid-cols-12',
          className,
        ].join(' ')}
        spacer={spacer}
      >
        <section className="grid gap-y-6 lg:col-span-7 justify-center justify-items-center lg:justify-items-start">
          <HeadingBody level="4" tagName="p" title={subTitle} />
          <Heading level="1" className="-mt-3 text-center lg:text-left">
            {title}
          </Heading>
          {description && (
            <WysiwygContent
              content={description}
              className={showBadges ? '' : 'md:!text-xl lg:!leading-loose'}
            />
          )}
          {showBadges &&
            (badges?.length > 0 ? (
              <div className="flex flex-col md:flex-row gap-x-6 xl:gap-x-8 gap-y-2 xl:mt-32">
                {badges.map(({ label, icon }, idx) => (
                  <article
                    key={idx}
                    className="flex gap-x-2 items-center justify-start xl:flex-col xl:border xl:rounded-2xl xl:border-white xl:bg-white xl:p-6 xl:gap-6 xl:w-48 xl:justify-items-center xl:mt-0"
                  >
                    {icon && (
                      <Image
                        src={icon.src}
                        className="w-5 xl:w-auto"
                        alt={icon.alt}
                        width={icon.width}
                        height={icon.height}
                      />
                    )}
                    <Paragraph className="xl:text-center text-sm xl:text-lg">
                      {label}
                    </Paragraph>
                  </article>
                ))}
              </div>
            ) : null)}
        </section>
        <section className="lg:col-span-5 grid gap-y-3 justify-items-center lg:justify-items-stretch">
          {formLabel && (
            <Paragraph size="large" className="text-gray-700">
              {formLabel}
            </Paragraph>
          )}
          <PartnerForm formId="partner" />
          <PartnerScripts />
        </section>
      </Wrap>
    </section>
  );
};

export default HeroPartner;
